import { useTranslation } from 'react-i18next';

import { type BadgeTone, environmentBadge, parseEnvironmentKind } from '@emr/shared';

/**
 * Which system is this?
 *
 * A safety control, not decoration. The *decision* of which tone/kind applies, and the English
 * fallback copy, still comes entirely from `shared/src/environmentBadge.ts` — nothing here
 * hardcodes a judgement about whether an environment holds PHI. That module exists precisely so a
 * wrong label is unrepresentable: a **subscriber test environment holds real patient data**, so it
 * carries the opposite of a "NO PHI" reassurance, and a component that wrote its own copy could get
 * that backwards in the one direction that matters.
 *
 * The *display text* is looked up here, by `badge.kind`, from `locales/<locale>/
 * environmentBanner.json` — a lookup keyed on the kind, deliberately layered on top of
 * `environmentBadge()` rather than changing what that function returns. `environmentBadge()` stays
 * untouched (still real, tested English strings) so nothing about its existing contract or tests
 * changes; this is a second, additive path for the one caller that needs a translated string.
 * `en/environmentBanner.json`'s own values are the same literal English `environmentBadge()`
 * already returns, kept in sync by hand since the two are deliberately independent sources — see
 * that JSON file if `BADGES` in `environmentBadge.ts` ever changes.
 *
 * The kind is read from configuration, never inferred from the hostname. `parseEnvironmentKind`
 * fails closed to `subscriber-test`, so a missing or misspelled value produces a red "may contain
 * real data" warning rather than a green all-clear or, worse, silence.
 *
 * Production renders nothing, and that is the module's decision rather than this component's: a
 * banner on every environment stops being read, so the signal is the exception. Production is also
 * the one kind deliberately absent from `environmentBanner.json` — `badge.visible` is checked
 * before either translated string is read, so a translation is never looked up for it.
 */

/**
 * Tone to appearance. A `Record` over the union, so a tone added to `@emr/shared` without a
 * treatment here is a build failure rather than an unstyled bar.
 *
 * `none` is unreachable in practice, because a badge with `tone: 'none'` also has
 * `visible: false` and returns before this map is consulted. It is present because the type says
 * it can exist, and a `default:` branch guessing at an appearance is how a future tone ships
 * looking like an informational notice when it is not one.
 */
const TONE_CLASSES: Record<BadgeTone, string> = {
  danger: 'bg-notice-danger text-notice-danger-ink',
  info: 'bg-notice-info text-notice-info-ink',
  none: 'hidden',
};

export function EnvironmentBanner() {
  const { t } = useTranslation('environmentBanner');
  const badge = environmentBadge(parseEnvironmentKind(import.meta.env.VITE_EMR_ENVIRONMENT));

  if (!badge.visible) return null;

  const label = t(`label.${badge.kind}`);
  const detail = t(`detail.${badge.kind}`);

  return (
    <aside
      // A landmark with its own name, so a screen-reader user can find it deliberately; it is also
      // first in the document, so they meet it before anything else on the page.
      aria-label={label}
      // `sticky` rather than `static`: scrolling a long chart must not scroll the warning away,
      // and a screenshot of any part of the page therefore still contains it.
      className={`sticky top-0 z-50 w-full px-4 py-2 ${TONE_CLASSES[badge.tone]}`}
      data-environment={badge.kind}
    >
      <div className="mx-auto flex w-full max-w-6xl flex-col gap-0.5 sm:flex-row sm:items-baseline sm:gap-4">
        <p className="m-0 shrink-0 text-base leading-tight font-extrabold tracking-wide sm:text-lg">
          {label}
        </p>
        <p className="m-0 text-sm leading-snug">{detail}</p>
      </div>
    </aside>
  );
}
